import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';

export type ButtonVariant =
  | 'primary'
  | 'accent'
  | 'outline'
  | 'ghost'
  | 'soft'
  | 'danger';

export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<
  ButtonVariant,
  { bg: string; fg: string; border: string; hover?: string }
> = {
  primary: { bg: 'var(--apm-ink)', fg: 'var(--apm-bg-2)', border: 'var(--apm-ink)' },
  accent:  { bg: 'var(--apm-accent)', fg: '#fff', border: 'var(--apm-accent)' },
  outline: { bg: 'transparent', fg: 'var(--apm-ink)', border: 'var(--apm-line-2)', hover: 'var(--apm-bg-sunken)' },
  ghost:   { bg: 'transparent', fg: 'var(--apm-ink-2)', border: 'transparent', hover: 'var(--apm-bg-sunken)' },
  soft:    { bg: 'var(--apm-bg-sunken)', fg: 'var(--apm-ink)', border: 'var(--apm-bg-sunken)' },
  danger:  { bg: 'var(--apm-risk)', fg: '#fff', border: 'var(--apm-risk)' },
};

const SIZES: Record<ButtonSize, { h: string | number; px: number; fs: number }> = {
  sm: { h: 28, px: 10, fs: 12.5 },
  md: { h: 'var(--apm-input-h)', px: 14, fs: 13.5 },
  lg: { h: 44, px: 18, fs: 14.5 },
};

export interface ButtonProps {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  style?: CSSProperties;
  title?: string;
}

export function Button({
  children,
  variant = 'ghost',
  size = 'md',
  icon,
  iconRight,
  onClick,
  disabled,
  type = 'button',
  style,
  title,
}: ButtonProps) {
  const v = VARIANTS[variant];
  const s = SIZES[size];
  const [hover, setHover] = useState(false);
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: s.h,
        padding: `0 ${s.px}px`,
        background: hover && v.hover && !disabled ? v.hover : v.bg,
        color: v.fg,
        border: `1px solid ${v.border}`,
        borderRadius: 'var(--apm-radius)',
        fontFamily: 'var(--apm-font-body)',
        fontSize: s.fs,
        fontWeight: 500,
        letterSpacing: '0.005em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background .12s, border-color .12s, opacity .12s',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}
